import React, { useEffect, useMemo } from "react";
import * as THREE from "three";
import {
  createHorizontalShapeEdgeGeometry,
  createVerticalShapeEdgeGeometry,
} from "../geometry";
import { disablePointerRaycast } from "../interaction/disablePointerRaycast";

const DEFAULT_EDGE_COLOR = 0x6f675e;
const DEFAULT_EDGE_OPACITY = 0.38;

type HiddenOccluderEdgesProps = {
  shape: THREE.Shape;
  mode: "horizontal" | "vertical";
  z?: number;
  height?: number;
  baseZ?: number;
  color?: THREE.ColorRepresentation;
  opacity?: number;
  userData?: Record<string, unknown>;
  renderOrder?: number;
  configureGeometry?: (geometry: THREE.BufferGeometry) => void;
};

const HiddenOccluderEdges: React.FC<HiddenOccluderEdgesProps> = ({
  shape,
  mode,
  z = 0,
  height = 0,
  baseZ = 0,
  color = DEFAULT_EDGE_COLOR,
  opacity = DEFAULT_EDGE_OPACITY,
  userData,
  renderOrder = 28,
  configureGeometry,
}) => {
  const geometry = useMemo(() => {
    const nextGeometry =
      mode === "horizontal"
        ? createHorizontalShapeEdgeGeometry(shape, z)
        : createVerticalShapeEdgeGeometry(shape, height, baseZ);
    configureGeometry?.(nextGeometry);
    return nextGeometry;
  }, [baseZ, configureGeometry, height, mode, shape, z]);

  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <lineSegments
      geometry={geometry}
      renderOrder={renderOrder}
      userData={userData}
      raycast={disablePointerRaycast}
    >
      <lineBasicMaterial
        color={color}
        transparent
        opacity={opacity}
        depthWrite={false}
        depthTest
        toneMapped={false}
      />
    </lineSegments>
  );
};

export default HiddenOccluderEdges;
